import { GameRenderer, GameResult } from '../../interfaces/game-type-plugin.interface';
import {
  DiceRollSettings,
  DiceRollState,
  DiceRollLegendSlot,
  DICE_ROLL_DEFAULT_PRIZE_COLORS,
  DICE_ROLL_PIXELS_PER_UNIT,
  DICE_ROLL_PROBABILITIES,
  autoAssignPrizesToDiceTotals
} from './dice-roll.types';
import { DiceRollSceneComponent } from './dice-roll-scene.component';

import { MockPrizeApiService } from '@kanek/shared-data-access';

// Constants
const WIN_CELEBRATION_DELAY_MS = 2000;

// Sound asset paths
const ASSET_PATHS = {
  WIN_SOUND: 'assets/sfx/scratchcard-win.mp3', // Reuse scratchcard win sound
  DRAMA_SOUND: 'assets/sfx/scratchcard-two-match.mp3', // Reuse scratchcard drama sound for 3rd ROLL AGAIN
} as const;

/**
 * Configuration object for DiceRollRenderer.updateConfig()
 */
export interface DiceRollRendererConfig {
  settings: DiceRollSettings;
  items: Array<{
    id?: string;
    label?: string;
    value?: string;
    icon?: string;
    probability?: number;
    [key: string]: unknown
  }>;
}

/**
 * Dice Roll renderer
 *
 * Handles legend generation, dice rolling, and Prize API integration.
 *
 * ARCHITECTURE:
 * - HTML/CSS: Legend layout with dice totals and prizes
 * - 3D: 2 Dice (Cannon.js physics), Table surface (box geometry with wood material)
 * - Game Logic: Single-roll instant results, prize-to-dice-total mapping
 */
export class DiceRollRenderer implements GameRenderer<DiceRollRendererConfig> {
  private container: HTMLElement | null = null;
  private config: DiceRollRendererConfig | null = null;
  private legendSlots: DiceRollLegendSlot[] = [];
  private gameState: DiceRollState | null = null;

  // Canvas store for accessing scene.userData (sceneGraph mode)
  private canvasStore: any | null = null;

  // ArService for keepVisible flag during critical moments
  private arService?: any;

  // Optional callbacks
  public onRoll?: () => void;
  public onLand?: (diceTotal: number) => void;
  public onWin?: (prizeId: string) => void;

  setCanvasStore(store: any): void {
    this.canvasStore = store;
  }

  setArService(arService: any): void {
    this.arService = arService;
  }

  /**
   * Initialize renderer with HTML container
   */
  initialize(container: HTMLElement, arService?: any): void {
    this.container = container;
    this.arService = arService;

    // Set container styles
    container.style.position = 'relative';
    container.style.width = '100%';
    container.style.height = '100%';
  }

  /**
   * Update configuration and regenerate legend
   */
  async updateConfig(config: DiceRollRendererConfig): Promise<void> {
    const previousConfig = this.config;
    this.config = config;

    // Only reset game state when:
    // 1. Explicit reset requested (_resetRequested flag)
    // 2. Prize list changed (items array)
    // 3. Prize mapping changed (affects gameplay)
    const resetRequested = (config.settings as any)._resetRequested;
    const prizesChanged = !previousConfig ||
      JSON.stringify(previousConfig.items) !== JSON.stringify(config.items);
    const mappingChanged = !previousConfig ||
      JSON.stringify(previousConfig.settings.prizeMapping) !== JSON.stringify(config.settings.prizeMapping);

    if (resetRequested || prizesChanged || mappingChanged) {
      this.gameState = null;

      // Clear winning-slot highlight and release keepVisible lock on explicit reset
      if (resetRequested) {
        this.removeWinningSlot();
      }

      // Trigger full visual reset (dice position, CTA button) whenever game state is cleared
      if (this.canvasStore) {
        const scene = this.canvasStore.scene;
        const resetGameFn = scene?.userData?.['resetGame'];
        if (resetGameFn && typeof resetGameFn === 'function') {
          resetGameFn();
        }
      }
    }

    // Validate minimum prizes
    if (config.items.length < 6) {
      throw new Error('Dice Roll requires at least 6 prizes');
    }

    // Generate legend layout
    this.legendSlots = this.generateLegend(config);

    // Render legend HTML
    if (this.container) {
      this.renderLegend(this.container);
    }

    // Update scene.userData if in sceneGraph mode
    if (this.canvasStore) {
      const scene = this.canvasStore.scene;
      if (scene) {
        scene.userData = scene.userData || {};
        scene.userData['config'] = config;
        scene.userData['legendSlots'] = this.legendSlots;
      }
    }
  }

  /**
   * Play the game (called by game-engine on CTA button click)
   *
   * For dice-roll, this performs a SINGLE ROLL with INSTANT RESULT.
   * No multi-roll state machine - one click = one complete game.
   */
  async play(options?: { getPrizeIndex?: () => Promise<number> }): Promise<GameResult | null> {
    if (!this.config) {
      throw new Error('Dice Roll not configured');
    }

    // Determine winning prize from Prize API
    let winningPrizeIndex: number;

    if (options?.getPrizeIndex) {
      // Production mode - call platform Prize API
      winningPrizeIndex = await options.getPrizeIndex();
    } else if ((this.config.settings as any)._previewPrizes) {
      // Preview mode - random selection
      winningPrizeIndex = Math.floor(Math.random() * this.config.items.length);
    } else {
      throw new Error('No Prize API callback and not in preview mode');
    }

    const winningPrize = this.config.items[winningPrizeIndex];

    // Get winning dice total from prize mapping
    const prizeMapping = this.config.settings.prizeMapping ||
      autoAssignPrizesToDiceTotals(this.config.items);

    // Find dice total for this prize index
    const winningDiceTotal = Object.entries(prizeMapping).find(
      ([, prizeIdx]) => prizeIdx === winningPrizeIndex
    )?.[0];

    if (!winningDiceTotal) {
      throw new Error(`No dice total mapped to prize index ${winningPrizeIndex}`);
    }

    const diceTotal = parseInt(winningDiceTotal);

    // Initialize game state
    this.gameState = {
      rollCount: 1,
      winningPrizeId: winningPrize.id || '',
      winningPrizeIndex,
      winningDiceTotal: diceTotal,
      isRolling: true,
      isComplete: false,
      hasWon: false
    };

    // Enable keepVisible for entire game session
    if (this.arService) {
      this.arService.keepVisible = true;
    }

    // Call onRoll callback (button disabled by game engine)
    this.onRoll?.();

    // Roll 2 dice with animation to show winning total
    await this.rollTwoDice(diceTotal);

    // Mark as complete and won
    this.gameState.isRolling = false;
    this.gameState.isComplete = true;
    this.gameState.hasWon = true;

    // Highlight winning legend slot
    this.highlightWinningSlot(diceTotal);

    // Trigger confetti celebration
    if (this.canvasStore) {
      const scene = this.canvasStore.scene;
      const confettiService = scene?.userData?.['confettiService'];
      if (confettiService) {
        confettiService.win();
      }
    }

    // Play win sound
    this.playSound(ASSET_PATHS.WIN_SOUND);

    // Call onLand and onWin callbacks
    this.onLand?.(diceTotal);
    this.onWin?.(this.gameState.winningPrizeId);

    // Set prizeRevealed flag after celebration delay
    setTimeout(() => {
      if (this.canvasStore) {
        const scene = this.canvasStore.scene;
        const appStore = scene?.userData?.['appStore'];
        if (appStore) {
          const session = appStore.session();
          appStore.setSession({
            ...session,
            prizeRevealed: true
          });
        }
      }

      // Disable keepVisible after celebration completes
      if (this.arService) {
        this.arService.keepVisible = false;
      }
    }, WIN_CELEBRATION_DELAY_MS);

    // Return result (game complete)
    return {
      isWinner: true,
      prize: {
        id: winningPrize.id || '',
        name: winningPrize.label || '',
        type: (winningPrize['type'] as string) || 'prize'
      },
      metadata: {
        prizeIndex: winningPrizeIndex,
        rollCount: 1,
        diceTotal
      }
    };
  }


  /**
   * Cleanup resources
   */
  destroy(): void {
    // Remove injected styles
    const styleElement = document.getElementById('dice-roll-styles');
    if (styleElement) {
      styleElement.remove();
    }

    this.removeWinningSlot();

    if (this.container) {
      this.container.innerHTML = '';
    }
    this.config = null;
    this.legendSlots = [];
    this.gameState = null;
  }

  /**
   * Reset the dice-roll game: dice return to a neutral resting state, the winning
   * legend slot highlight is cleared, and the next `play()` call starts a fresh game.
   *
   * Uses the existing `_resetRequested` flag so `updateConfig()` handles both the
   * 3D scene reset (dice positions) and the HTML legend re-render in one pass.
   *
   * Preview-only: called from GameEngineComponent.resetGame() guarded by previewMode.
   */
  async reset(): Promise<void> {
    if (!this.config) return;

    await this.updateConfig({
      ...this.config,
      settings: { ...this.config.settings, _resetRequested: true } as DiceRollSettings,
    });
  }

  /**
   * Get scene component for sceneGraph mode
   */
  getSceneComponent() {
    return DiceRollSceneComponent;
  }

  // ==================== PRIVATE METHODS ====================

  /**
   * Generate legend slots from prize list using auto-assignment algorithm
   */
  private generateLegend(config: DiceRollRendererConfig): DiceRollLegendSlot[] {
    const slots: DiceRollLegendSlot[] = [];
    const colors = config.settings.prizeColors || DICE_ROLL_DEFAULT_PRIZE_COLORS;

    // Get prize mapping (manual override or auto-assignment)
    const prizeMapping = config.settings.prizeMapping ||
      autoAssignPrizesToDiceTotals(config.items);

    // Create legend slots for dice totals 2-12
    for (let diceTotal = 2; diceTotal <= 12; diceTotal++) {
      const prizeIndex = prizeMapping[diceTotal];
      const prize = config.items[prizeIndex];
      const slotIndex = diceTotal - 2; // 0-10 for color indexing

      slots.push({
        diceTotal,
        prizeId: prize?.id,
        prizeIndex,
        prizeName: prize?.label,
        prizeValue: prize?.value,
        prizeIcon: prize?.icon,
        color: colors[slotIndex % colors.length],
        probability: DICE_ROLL_PROBABILITIES[diceTotal]
      });
    }

    return slots;
  }


  /**
   * Render legend HTML
   */
  private renderLegend(container: HTMLElement): void {
    if (!this.config) return;

    container.innerHTML = `
      <div class="dice-roll-legend">
        ${this.legendSlots.map(slot => this.renderLegendSlot(slot)).join('')}
      </div>
    `;

    // Add styles
    this.injectLegendStyles();

    // Note: ROLL button will be added as separate UI overlay by game-engine
  }

  /**
   * Render individual legend slot HTML
   */
  private renderLegendSlot(slot: DiceRollLegendSlot): string {
    return `
      <div class="legend-slot" data-dice-total="${slot.diceTotal}" style="background-color: ${slot.color}">
        <div class="dice-total-display">
          <div class="dice-total-number">${slot.diceTotal}</div>
          <div class="dice-total-probability">${(slot.probability * 100).toFixed(1)}%</div>
        </div>
        <div class="prize-info">
          ${slot.prizeIcon ? `<img src="${slot.prizeIcon}" class="prize-icon" alt="${slot.prizeName}" />` : ''}
          <div class="prize-details">
            <div class="prize-name">${slot.prizeName || 'No Prize'}</div>
            <div class="prize-value">${slot.prizeValue || ''}</div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Inject CSS styles for legend layout
   */
  private injectLegendStyles(): void {
    const styleId = 'dice-roll-styles';

    // Remove existing styles to allow regeneration when settings change
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Get surface size for styling calculations
    const surfaceSize = this.config?.settings.surfaceSize || 'medium';
    const pixelsPerUnit = DICE_ROLL_PIXELS_PER_UNIT[surfaceSize];

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .dice-roll-legend {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-auto-rows: minmax(auto, 1fr);
        gap: ${Math.floor(pixelsPerUnit * 0.3)}px;
        padding: ${Math.floor(pixelsPerUnit * 0.5)}px;
        user-select: none;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        background: url('assets/images/felt.jpg') center center / cover no-repeat;
      }

      .legend-slot {
        display: flex;
        align-items: center;
        gap: ${Math.floor(pixelsPerUnit * 0.4)}px;
        padding: ${Math.floor(pixelsPerUnit * 0.3)}px;
        border: 2px solid rgba(0, 0, 0, 0.2);
        border-radius: ${Math.floor(pixelsPerUnit * 0.2)}px;
        background-color: inherit;
        transition: all 0.3s ease;
        box-sizing: border-box;
      }

      /* Last slot (12) spans full width */
      .legend-slot:last-child {
        grid-column: 1 / -1;
      }

      .legend-slot:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
      }

      .dice-total-display {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-width: ${Math.floor(pixelsPerUnit * 1.5)}px;
        background: rgba(255, 255, 255, 0.3);
        border-radius: ${Math.floor(pixelsPerUnit * 0.15)}px;
        padding: ${Math.floor(pixelsPerUnit * 0.2)}px;
      }

      .dice-total-number {
        font-size: ${Math.floor(pixelsPerUnit * 0.8)}px;
        font-weight: bold;
        color: #fff;
        text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.75);
      }

      .dice-total-probability {
        font-size: ${Math.floor(pixelsPerUnit * 0.4)}px;
        color: rgba(255, 255, 255, 0.9);
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.75);
      }

      .prize-info {
        display: flex;
        align-items: center;
        gap: ${Math.floor(pixelsPerUnit * 0.3)}px;
        flex: 1;
      }

      .prize-icon {
        width: ${Math.floor(pixelsPerUnit * 1.2)}px;
        height: ${Math.floor(pixelsPerUnit * 1.2)}px;
        object-fit: contain;
      }

      .prize-details {
        display: flex;
        flex-direction: column;
      }

      .prize-name {
        font-size: ${Math.floor(pixelsPerUnit * 0.5)}px;
        font-weight: bold;
        color: #fff;
        text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.75);
      }

      .prize-value {
        font-size: ${Math.floor(pixelsPerUnit * 0.4)}px;
        color: rgba(255, 255, 255, 0.9);
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.75);
      }

      /* Winning slot highlight */
      .legend-slot.winning-slot {
        animation: winningPulse 1s ease-in-out infinite;
        z-index: 10;
      }

      @keyframes winningPulse {
        0%, 100% {
          box-shadow: 0 0 15px 8px rgba(76, 175, 80, 0.8),
                      0 0 30px 12px rgba(76, 175, 80, 0.6);
          border-color: #4CAF50;
          border-width: 3px;
          transform: scale(1);
        }
        50% {
          box-shadow: 0 0 25px 12px rgba(76, 175, 80, 1),
                      0 0 50px 20px rgba(76, 175, 80, 0.8);
          border-color: #66BB6A;
          border-width: 4px;
          transform: scale(1.1);
        }
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * Roll 2 dice with animation to display target total.
   * The physics simulation will find ANY combination that totals to the target.
   */
  private async rollTwoDice(targetTotal: number): Promise<void> {
    // Trigger 3D dice roll animation via scene
    // The physics simulation will accept ANY combination that totals to targetTotal
    // (e.g., for total 7: 1+6, 2+5, 3+4, 4+3, 5+2, or 6+1 are all valid)
    if (this.canvasStore) {
      const scene = this.canvasStore.scene;
      const rollDiceFn = scene?.userData?.['rollDice'];
      if (rollDiceFn && typeof rollDiceFn === 'function') {
        const result = await rollDiceFn(targetTotal);

        // Update game state with ACTUAL dice values from simulation
        if (this.gameState && result?.values) {
          this.gameState.currentDiceValues = result.values;
        } else if (this.gameState) {
          console.warn('[DICE ROLL] No result returned from rollDice');
        }
      } else {
        // Fallback if scene not ready
        console.warn('[DICE ROLL] Scene not ready, using fallback delay');
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    } else {
      // Fallback if no canvas store
      console.warn('[DICE ROLL] No canvas store, using fallback delay');
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  }

  /**
   * Highlight the winning legend slot with glow effect
   */
  private highlightWinningSlot(diceTotal: number): void {
    const legendContainer = this.container?.querySelector('.dice-roll-legend') ||
      document.querySelector('.dice-roll-legend');

    if (!legendContainer) return;

    const winningSlot = legendContainer.querySelector(`[data-dice-total="${diceTotal}"]`) as HTMLElement;
    if (winningSlot) {
      winningSlot.classList.add('winning-slot');
    }
  }

  /**
   * Remove winning-slot highlight from all legend slots.
   * Called on reset so a fresh game starts with a clean legend.
   */
  private removeWinningSlot(): void {
    const legendContainer = this.container?.querySelector('.dice-roll-legend') ||
      document.querySelector('.dice-roll-legend');

    if (!legendContainer) return;

    const winningSlot = legendContainer.querySelector('.winning-slot');
    if (winningSlot) {
      winningSlot.classList.remove('winning-slot');
    }

    // Release AR keepVisible flag
    if (this.arService) {
      this.arService.keepVisible = false;
    }
  }

  /**
   * Play sound effect
   */
  private playSound(src: string): void {
    if (this.canvasStore) {
      const scene = this.canvasStore.scene;
      const audioService = scene?.userData?.['audioService'];
      if (audioService) {
        audioService.play(src, { volume: 0.7 });
      }
    }
  }
}
